import { useEffect, useState } from 'react';
import { Truck, Loader2, Calendar, Hash, Package, MapPin, ExternalLink } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { purchasingAPI, AdvancedShippingNotice } from '../../lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useFormatters } from '../../contexts/PreferencesContext';
import { asnDisplayStatus } from './asnDeliveryType';
import { useColumnSort } from '../../hooks/useColumnSort';
import { SortableTh } from '../common/SortableTh';
import { courierLabel } from './courierLabel';

const TRANSPORT_LABELS: Record<string, string> = {
  road: 'Road', rail: 'Rail', air: 'Air', sea: 'Sea',
};
const ASN_STATUS_LABELS: Record<string, string> = {
  created: 'Created',
  submitted: 'Submitted',
  // CR-7142: an acknowledged direct load -- left the supplier, but never
  // enters In Transit because it does not come to a customer warehouse.
  acknowledged: 'Acknowledged',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  received: 'Received',
  cancelled: 'Cancelled',
};


const getTransportLabel = (method: string | undefined) => TRANSPORT_LABELS[method || ''] || method || '-';
const getAsnStatusLabel = (status: string) => ASN_STATUS_LABELS[status] || ASN_STATUS_LABELS['created'];

/**
 * CR-7263: sort keys for the columns that do not display a raw field.
 *
 * Three of these columns render a label looked up from a code, and the codes
 * do not order the same way as the labels they show (`in_transit` sorts before
 * `submitted`; "In Transit" sorts before "Submitted" too, but `dhl` before
 * `fedex` while "DHL" also precedes "FedEx" -- and `other`/`Other` only agree
 * by luck). A column must sort by what the reader can see, so each derives its
 * key the same way the cell derives its text.
 *
 * Module scope, not inline: the hook memoises on this object's identity, so a
 * fresh literal every render would re-sort every render.
 */
const ASN_SORT_KEYS = {
  status: (asn: AdvancedShippingNotice) => getAsnStatusLabel(asnDisplayStatus(asn)),
  // CR-7732: sorted by what the column shows, so a named carrier does not
  // sort under "Other" while reading as itself.
  courier: (asn: AdvancedShippingNotice) => courierLabel(asn.courier, asn.courier_other),
  method_of_transport: (asn: AdvancedShippingNotice) => getTransportLabel(asn.method_of_transport),
} as const;

interface POLineASNTabProps {
  lineId: number;
}

interface ASNSummary {
  total_asns: number;
  total_shipped: string;
  asns: AdvancedShippingNotice[];
}

export default function POLineASNTab({ lineId }: POLineASNTabProps) {
  const [summary, setSummary] = useState<ASNSummary | null>(null);
  // CR-7263: one line's rows, fully loaded and unpaginated -- sorting the
  // array is the whole answer, no ordering parameter (R-056/R-057).
  // Declared above the loading/error early returns: a hook below one of
  // those is called on some renders and not others.
  const { sortedRows: sortedAsns, sortField, sortDirection, onSort } = useColumnSort(
    summary?.asns ?? [],
    // `shipment_date` is the field the Ship Date column renders; there is no
    // `ship_date` on the ASN shape.
    { dateFields: ['shipment_date', 'estimated_delivery_date'], computed: ASN_SORT_KEYS },
  );


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchASNSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const fetchASNSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getLineASNs(lineId);
      setSummary({
        total_asns: response.data.total_asns,
        total_shipped: response.data.total_shipped,
        asns: response.data.asns,
      });
    } catch (err: unknown) {
      console.error('Failed to fetch ASN summary:', err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || 'Failed to load ASN summary');
      toast.error('Failed to load ASN summary');
    } finally {
      setLoading(false);
    }
  };

  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { className: string; icon: LucideIcon }> = {
      created: { className: 'bg-gray-100 text-gray-800 border-gray-200', icon: Package },
      submitted: { className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Truck },
      acknowledged: { className: 'bg-purple-100 text-purple-800 border-purple-200', icon: Truck },
      in_transit: { className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Truck },
      delivered: { className: 'bg-green-100 text-green-800 border-green-200', icon: MapPin },
      received: { className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: Package },
      cancelled: { className: 'bg-red-100 text-red-800 border-red-200', icon: Package },
    };

    const config = statusConfig[status] || statusConfig['created'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${config.className}`}>
        <Icon className="h-3 w-3" />
        {getAsnStatusLabel(status)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Truck className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchASNSummary}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!summary || summary.total_asns === 0) {
    return (
      <div className="text-center py-12">
        <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground font-medium">No shipments found for this PO line</p>
        <p className="text-sm text-muted-foreground mt-2">
          Advanced Shipping Notices will appear here once shipments are created
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Total Shipments</span>
            <Truck className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-900">{summary.total_asns}</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-900">Total Qty Shipped</span>
            <Package className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">{parseFloat(summary.total_shipped).toLocaleString()}</p>
        </div>
      </div>

      {/* ASN List */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4">Shipment History</h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <SortableTh field="asn_number" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">ASN Number</SortableTh>
                <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Status</SortableTh>
                <SortableTh field="courier" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Courier</SortableTh>
                <SortableTh field="method_of_transport" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Transport</SortableTh>
                <SortableTh field="tracking_number" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Tracking</SortableTh>
                <SortableTh field="shipment_date" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Ship Date</SortableTh>
                <SortableTh field="estimated_delivery_date" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Est. Delivery</SortableTh>
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedAsns.map((asn, index) => (
                <tr
                  key={asn.id}
                  className={`hover:bg-muted/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      <Link to={`/purchasing?tab=asn&asn=${asn.id}`} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">{asn.asn_number}</Link>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(asnDisplayStatus(asn))}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{courierLabel(asn.courier, asn.courier_other)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{getTransportLabel(asn.method_of_transport)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                      {asn.tracking_number || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{formatDate(asn.shipment_date)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{formatDate(asn.estimated_delivery_date)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/purchasing?tab=asn&asn=${asn.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      View
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Carbon Emissions Summary (if available) */}
      {summary.asns.some(asn => asn.carbon_emission_kg) && (
        <div className="bg-muted/30 border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-muted-foreground">Environmental Impact</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Total Carbon Emissions</p>
              <p className="text-xl font-bold text-primary">
                {summary.asns.reduce((sum, asn) => sum + parseFloat(asn.carbon_emission_kg || '0'), 0).toFixed(2)} kg CO₂
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Total Weight Shipped</p>
              <p className="text-xl font-bold text-primary">
                {summary.asns.reduce((sum, asn) => sum + parseFloat(asn.weight_kg || '0'), 0).toFixed(2)} kg
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
